import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ORIENT_SECTIONS, ORIENT_STORE_NAME } from "../lib/catalog";
import Header from "../components/Header";
import { MapPin, Zap, ChevronRight } from "lucide-react";

const CITIES = ["Norrköping", "Norrköping – Centrum", "Norrköping – Hageby", "Norrköping – Ingelsta", "Norrköping – Klockaretorpet", "Norrköping – Navestad"];

const CitySelect = () => {
  const { t, city, setCity } = useApp();
  const [choice, setChoice] = useState(CITIES[0]);
  if (city) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-background flex items-center justify-center p-4" data-testid="city-select-overlay">
      <div className="w-full max-w-md text-center animate-ha-up">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-primary text-primary-foreground grid place-items-center mb-5 shadow-lg shadow-primary/25"><MapPin className="h-8 w-8" /></div>
        <h1 className="font-head text-3xl font-extrabold mb-1">{t.chooseCity}</h1>
        <p className="text-sm text-muted-foreground mb-6">{t.cityHint}</p>
        <div className="space-y-2 mb-5 text-start">
          {CITIES.map((c) => (
            <button key={c} data-testid={`city-option-${c}`} onClick={() => setChoice(c)}
              className={`w-full h-12 rounded-xl border px-4 flex items-center gap-2 font-medium transition-colors ${choice === c ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:bg-accent"}`}>
              <MapPin className="h-4 w-4" /> {c}
            </button>
          ))}
        </div>
        <button data-testid="city-confirm-button" onClick={() => setCity(choice)} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">{t.continue}</button>
      </div>
    </div>
  );
};

export default function Home() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const sections = ORIENT_SECTIONS.filter((s) => s.slug !== "ovrigt");

  return (
    <div className="min-h-screen">
      <CitySelect />
      <Header />

      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="max-w-2xl animate-ha-up">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-4"><Zap className="h-3.5 w-3.5" /> {t.deliveryIn} · Norrköping</span>
            <div className="flex items-center gap-4">
              <img src="/orient-logo.png" alt="Orient Livsmedel" className="h-16 sm:h-20 w-auto rounded-xl bg-white p-2 shadow-sm" />
              <div>
                <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">{ORIENT_STORE_NAME}</h1>
                <p className="mt-1 text-base text-muted-foreground">{t.heroSub}</p>
              </div>
            </div>
            <button data-testid="open-store-button" onClick={() => navigate("/store/orient")} className="mt-6 h-12 px-6 rounded-2xl bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25 hover:opacity-90 transition-opacity inline-flex items-center gap-2">
              {t.open} {ORIENT_STORE_NAME} <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </button>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-head text-xl sm:text-2xl font-bold mb-5">{lang === "sv" ? "Avdelningar" : "الأقسام"}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {sections.map((s, i) => (
            <button key={s.slug} data-testid={`section-card-${s.slug}`} onClick={() => navigate(`/store/orient?sektion=${s.slug}`)}
              style={{ animationDelay: `${Math.min(i * 50, 500)}ms` }}
              className="group text-start rounded-2xl overflow-hidden border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5 animate-ha-up">
              <div className="aspect-[16/10] grid place-items-center text-5xl" style={{ background: `linear-gradient(135deg, ${s.color}22, ${s.color}55)` }}>{s.emoji}</div>
              <div className="p-4 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-head font-semibold truncate">{s.sv}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.ar}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 rtl:rotate-180 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
